"use client";

import React, { useEffect, useRef, useState } from "react";
import { Maximize2 } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_neon_vibes.png",
  caption: "Kawaii Desk Setup",
  ratio: 4 / 3
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_perfect_sear.png",
  caption: "The 3D Print Detail",
  ratio: 1 / 1
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_crowd_cheers.png",
  caption: "Montreal Pop-Up",
  ratio: 3 / 4
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_industrial_wall.png",
  caption: "Studio Vibes",
  ratio: 16 / 9
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_double_smash.png",
  caption: "Pocket-Sized Joy",
  ratio: 4 / 5
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_kitchen_heat.png",
  caption: "Limited Drop",
  ratio: 1 / 1
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_golden_fries.png",
  caption: "Sensory Satisfaction",
  ratio: 4 / 3
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1643032/img/yuju_late_night.png",
  caption: "Fidget Collection",
  ratio: 3 / 4
}];
export default function VibrantMoments() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: galleryRef,
    isVisible: isGalleryVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223917" id="as-seen-in-the-wild" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={`text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Badge variant="outline" className="mb-4 text-primary border-primary/40 font-default uppercase tracking-widest bg-white/50 backdrop-blur-sm">
            Fidget Fashion
          </Badge>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-default font-bold tracking-tight text-foreground mb-6 drop-shadow-sm">
            As Seen <span className="text-primary">in the Wild</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default">
            Tiny toys, big serotonin. See how our community clicks, pops, and styles their favorite Petit Pop collectibles.
          </p>
        </div>

        {}
        <div ref={galleryRef} className={`columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6 space-y-4 sm:space-y-6 transition-all duration-1000 delay-200 ease-out ${isGalleryVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {galleryItems.map((item, i) => <div key={item.id} data-index={i} className="break-inside-avoid relative group">
              <Dialog>
                <DialogTrigger className="w-full block overflow-hidden rounded-2xl bg-card p-3 sm:p-4 border border-border/50 outline-none focus-visible:ring-4 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background cursor-zoom-in shadow-md hover:shadow-2xl transition-all duration-300 transform group-hover:-rotate-1">
                  <AspectRatio ratio={item.ratio}>
                    <img src={item.src} alt={item.caption} className="h-full w-full object-cover rounded-lg transition-transform duration-700 ease-out group-hover:scale-102" loading="lazy" />
                    {}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 rounded-lg" />

                    {}
                    <div className="absolute inset-0 p-6 flex flex-col justify-end items-start opacity-0 transition-all duration-300 group-hover:opacity-100">
                      <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 ease-out">
                        <Badge variant="secondary" className="font-default uppercase tracking-wider text-xs sm:text-sm shadow-md mb-2 bg-white text-foreground border-none">
                          {item.caption}
                        </Badge>
                      </div>
                    </div>

                    {}
                    <div className="absolute top-4 right-4 bg-primary/80 backdrop-blur-md p-2.5 rounded-full opacity-0 transform scale-75 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100 shadow-lg">
                      <Maximize2 className="size-4 text-primary-foreground" />
                    </div>
                  </AspectRatio>
                </DialogTrigger>

                {}
                <DialogContent className="max-w-5xl bg-background/95 backdrop-blur-xl border-border shadow-4xl p-1 sm:p-2 rounded-3xl" showCloseButton={false}>
                  <div className="relative w-full overflow-hidden rounded-2xl bg-muted flex items-center justify-center min-h-[50vh]">
                    <img src={item.src} alt={item.caption} className="w-full h-auto max-h-[85vh] object-contain" />
                    <DialogClose className="absolute top-4 right-4 bg-primary/90 hover:bg-primary text-primary-foreground p-2 rounded-full backdrop-blur-md transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <span className="sr-only">Close</span>
                      <Maximize2 className="size-5 rotate-45" />
                    </DialogClose>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
                      <Badge variant="default" className="font-default uppercase tracking-widest text-sm px-4 py-1.5 shadow-xl bg-primary text-primary-foreground rounded-full">
                        {item.caption}
                      </Badge>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>)}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button size="lg" className="font-default uppercase tracking-wider text-base h-14 px-10 rounded-full bg-accent text-accent-foreground shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300" asChild>
            <a href="/ShopPage">Shop the Collection</a>
          </Button>
        </div>
      </div>
    </section>;
}